"use client";
// eslint-disable-next-line no-restricted-imports
import { hotjar as hotjarLib } from "react-hotjar";
import { useReactiveVar } from "@apollo/client/react";
import { accessTokenVar } from "../apolloUtils";
import { decodeJwt, TokenInfo } from "@portal/app/_common/lib/token";
import { isProduction } from "@portal/app/_common/lib/edgeUtils";
import { useEffect } from "react";

type HotjarIdentity = Parameters<typeof hotjarLib.identify>;
let pendingIdentity: { args: HotjarIdentity; accessToken: string | undefined } | undefined;

/**
 * The Hotjar library's functions throw errors if Hotjar is not initialized.
 * Since we aren't initializing Hotjar on all pages, this let's the errors be
 * caught and ignored.
 * @param methodName
 */
const safeHotjarFn =
  <Method extends keyof typeof hotjarLib>(methodName: Method) =>
  (...args: Parameters<(typeof hotjarLib)[Method]>) => {
    try {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return hotjarLib[methodName](...args);
    } catch {
      return;
    }
  };

export const hotjar = {
  initialize: (...args: Parameters<typeof hotjarLib.initialize>) => {
    safeHotjarFn("initialize")(...args);
    if (!hotjarLib.initialized() || !pendingIdentity) {
      return;
    }
    const pending = pendingIdentity;
    pendingIdentity = undefined;
    const accessToken = accessTokenVar();
    const previousUser = decodeJwt<TokenInfo>(pending.accessToken);
    const currentUser = decodeJwt<TokenInfo>(accessToken);
    // Token refresh is allowed, but logout/account/role changes must not replay
    // the previous user's details into the new Hotjar session.
    if (
      accessToken === pending.accessToken ||
      (previousUser?.userId &&
        previousUser.userId === currentUser?.userId &&
        previousUser.activeRole === currentUser.activeRole)
    ) {
      safeHotjarFn("identify")(...pending.args);
    }
  },
  identify: (...args: HotjarIdentity) => {
    if (!hotjarLib.initialized()) {
      pendingIdentity = { args, accessToken: accessTokenVar() };
      return;
    }
    pendingIdentity = undefined;
    return safeHotjarFn("identify")(...args);
  },
  event: safeHotjarFn("event"),
  stateChange: safeHotjarFn("stateChange"),
};

export const HotjarProvider = () => {
  const accessToken = useReactiveVar(accessTokenVar);
  const isAdmin = decodeJwt<TokenInfo>(accessToken)?.activeRole === "ADMIN";

  useEffect(() => {
    if (isProduction() && !isAdmin) {
      hotjar.initialize({ id: 2333783, sv: 6, debug: true }); // Can be found in the tracking code script
    }
  }, [isAdmin]);

  return null;
};
